<template>
  <div class="full-page-width relative locations">
    <div class="row">
      <div class="col-xs-4 text-box">
        <InfoTextBox
          :text="$t('location-info-text')"
          :title="$t('Locations')"
          :btnText="$t('Add location')"
          @action="openNewPopup" />
      </div>
      <div class="col-xs-8">
        <ul class="list-table">
          <li
            class="list-table-item"
            v-for="(item, index) in get_senders.items" :key="`item-${index}`"
            @click="openEditPopup(item.id)">
            <div class="table-cell small">
              <div class="circled-icon flags">
                <CountryFlag :country='getFlag(item.country_id)' size='normal'/>
              </div>
            </div>
            <div class="table-cell">
              <p>
              <span class="main-info">
                <span v-if="item.company_name !== null">
                  {{ `${item.company_name}, ` }}
                </span>
               {{ `${item.name}, ${item.address_1}, ${item.post_code} ${item.city}, ${getCountryName(item.country_id)}` }}
              </span>
                <span class="additional-info">
                  {{ get_senders.entity.type[item.type] }}
                </span>
            </p>
            </div>
            <div class="table-cell small" v-show="item.is_default">
              <div class="list-icon-block">
                <Icon
                  :circle=true
                  name="star"
                  size="20"/>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <Popup
      v-if="openPopup"
      :popupMedium=true
      :isPopupSubmitDisabled="isPopupSubmitDisabled"
      :showConfirmButton="!deleteLocation"
      :showDeleteButton="deleteLocation"
      @cancel="togglePopup"
      @deleteAction="deleteLocationAction"
      @confirm="submitForm">
      <template v-slot:header>
        <h2 v-if="!isEdit">{{ $t('Add location') }}</h2>
        <h2 v-if="isEdit">{{ $t('Edit location') }}</h2>
      </template>
      <div>
        <div class="row" v-if="get_user.is_admin">
          <div class="col-xs-12">
            <InputSelect
              class="m-b-20"
              :multiSelect=true
              :preselected="form.accessible_for_subaccounts"
              :label="$t('accessible_for_accounts')"
              @change="selectAccessibleSubaccount"
              :options="getAllowedSubaccounts"
              :rules="{required: true}" />
          </div>
        </div>
        <div class="row">
          <div class="col-xs-12">
            <div class="flex-center toggle-title">
              <div>
                <p>{{ $t('is_default') }}</p>
                <span>{{ $t('Will be selected as shipper on booking page') }}</span>
              </div>
              <div>
                <Toggle id="is_default" v-model="form.is_default" />
              </div>
            </div>
          </div>
        </div>
        <div class="separator medium"></div>
        <div class="row">
          <div class="col-xs-12 m-b-20">
            <InputSelect
              :label="$t('type')"
              :multiSelect=true
              :preselected="form.type"
              :rules="{required: true}"
              @change="selectType"
              :options="selectOptions(get_senders.entity['type'])" />
          </div>
          <div class="col-xs-12 m-b-20">
            <InputSelect
              :label="$t('contact_type')"
              v-model="form.contact_type"
              :rules="{required: true}"
              :options="selectOptions(get_senders.entity['contact_type'])" />
          </div>
          <div class="col-xs-12 m-b-20" v-if="parseInt(form.contact_type) === 1">
            <InputText
              :label="$t('company_name')"
              v-model="form.company_name"
              :rules="{required: parseInt(form.contact_type) === 1}" />
          </div>
          <div class="col-xs-12 m-b-20">
            <InputText
              :label="$t('name')"
              v-model="form.name"
              :rules="{required: false}" />
          </div>
          <div class="col-xs-12 m-b-20">
            <InputText
              :label="$t('vat')"
              v-model="form.vat"
              :rules="{required: false}" />
          </div>
        </div>
        <div>
          <AddressPicker
            class="m-b-20"
            :label="$t('address_1')"
            @addressSelected="changeAddress"
            v-model="formAddress" />
        </div>
        <div class="row">
          <div class="col-xs-6">
            <PhoneSelect v-model="form.phone" :rules="{required: false}" />
          </div>
          <div class="col-xs-6">
            <InputText
              label="Email"
              v-model="form.email"
              :rules="{required: false, email: false}" />
          </div>
        </div>
        <div class="separator medium"></div>
        <div class="col-xs-12 p-b-20" v-if="isEdit">
          <div class="flex-center toggle-title">
            <div>
              <p>{{ $t('delete') }}</p>
              <span>{{ $t('Location will be deleted') }}</span>
            </div>
            <div>
              <Toggle color="red" id="delete" v-model="deleteLocation" />
            </div>
          </div>
        </div>
      </div>
    </Popup>
  </div>
</template>
<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
import mixin from '@/mixins/mixins.js';
import AddressPicker from "@/components/global/addressPicker/index.vue";
import PhoneSelect from "@/components/elements/inputs/phone.vue";
import CountryFlag from 'vue-country-flag';
import Btn from '@/components/elements/buttons';
import InputText from '@/components/elements/inputs/text.vue';
import Popup from '@/components/global/popup';
import InputSelect from '@/components/elements/inputs/select.vue';
import Toggle from '@/components/elements/inputs/toggle.vue';
import countryList from '@/data/country.js';

import InfoTextBox from '@/components/global/infoTextBox';
import formValidator from '@/mixins/formValidator';

export default {
  name: 'Senders',
  mixins: [mixin, formValidator],
  components: {
    Btn,
    AddressPicker,
    PhoneSelect,
    Popup,
    InputText,
    CountryFlag,
    InputSelect,
    Toggle,
    InfoTextBox,
  },
  data() {
    return {
      openPopup: false,
      deleteLocation: false,
      isEdit: false,
      senderId: '',
      formAddress: {
        address_1: '',
        address_2: '',
        post_code: '',
        city: '',
        country_id: '',
        state: '',
      },
      form: {
        accessible_for_subaccounts: [],
        name: '',
        company_name: '',
        phone: {
          number: '',
          country: '',
        },
        email: '',
        vat: '',
        is_default: '',
        type: [],
        contact_type: '',
      },
    };
  },
  computed: {
    ...mapGetters([
      'get_senders',
      'get_sender_by_id',
      'get_subaccounts',
      'get_user'
    ]),
    isPopupSubmitDisabled() {
      if(this.get_user.is_admin && this.form.accessible_for_subaccounts.length === 0) return true;
      if (this.form.type.length === 0) return true;

      let formWithValidators = [
        {
          formFeild:'address_1',
          value: this.formAddress.address_1,
          validators:{required: true}
        },
        {
          formFeild:'post_code',
          value: this.formAddress.post_code,
          validators:{required: true}
        },
        {
          formFeild:'city',
          value: this.formAddress.city,
          validators:{required: true}
        },
        {
          formFeild:'country_id',
          value: this.formAddress.country_id,
          validators:{required: true}
        },
        {
          formFeild:'contact_type',
          value: this.form.contact_type,
          validators:{required: true}
        },
      ]
      if(this.form.contact_type == 1){
        formWithValidators.push({
          formFeild:'company_name',
          value: this.form.company_name,
          validators:{required: true}
        })
      } else {
        formWithValidators.filter((item) => item.formFeild != 'company_name')
      }

      return this.validateInput(formWithValidators);
    },
    getAllowedSubaccounts() {
      return this.get_subaccounts.filter((acc) => this.get_user.accessible_subaccounts_array.indexOf(String(acc.id)) > -1).map((acc) => ({text: acc.name, value: String(acc.id),}));
    },
  },
  created() {
    this.$store.dispatch('GET_SENDERS');
  },
  methods: {
    selectAccessibleSubaccount(val) {
      if(val.length > 0){
        this.form.accessible_for_subaccounts = val;
      }
    },
    selectType(val) {
      if(val.length > 0){
        this.form.type = val;
      }
    },
    getFlag(id) {
      return countryList.filter((opt) => opt.id === id.toString())[0].alpha2;
    },
    getCountryName(id) {
      return countryList.filter((opt) => opt.id === id.toString())[0].name;
    },
    selectOptions(obj) {
      return Object.entries(obj).map(([key, value]) => {
        return { text: value, value: key };
      });
    },
    changeAddress(val) {
      this.formAddress.address_1 = val.address_1;
      this.formAddress.address_2 = val.address_2 || '';
      this.formAddress.country_id = val.country_id;
      this.formAddress.post_code = val.post_code;
      this.formAddress.city = val.city;
      this.formAddress.state = val.state;

      this.form.phone.country = this.getFlag(val.country_id);
    },
    clearLocalFormData() {
      this.formAddress = {
        address_1: '',
        address_2: '',
        post_code: '',
        city: '',
        country_id: '',
        state: '',
      };
      this.form = {
        accessible_for_subaccounts: [],
        name: '',
        company_name: '',
        phone: {
          number: '',
          country: '',
        },
        email: '',
        vat: '',
        is_default: false,
        type: [],
        contact_type: '',
      };
    },
    togglePopup() {
      this.openPopup = !this.openPopup;
      this.senderId = '';
      this.clearLocalFormData();
    },
    submitForm() {
      const action = this.isEdit ? 'EDIT_SENDER' : 'SET_SENDER';
      this.form.accessible_for_subaccounts = this.form.accessible_for_subaccounts.filter((val) => this.get_user.accessible_subaccounts_array.indexOf(String(val)) > -1);

      this.$store.dispatch(action, {
        form: {
          accessible_for_subaccounts: this.form.accessible_for_subaccounts,
          address_1: this.formAddress.address_1,
          address_2: this.formAddress.address_2,
          post_code: this.formAddress.post_code,
          city: this.formAddress.city,
          country_id: this.formAddress.country_id,
          state: this.formAddress.state,
          name: this.form.name,
          company_name: this.form.company_name,
          phone: this.form.phone.number,
          email: this.form.email,
          vat: this.form.vat,
          is_default: Number(this.form.is_default),
          type: this.form.type,
          contact_type: this.form.contact_type,
        },
        id: this.senderId || '',
      }).then(() => {
        this.$store.dispatch('GET_SENDERS');
        this.openPopup = false;
        this.clearLocalFormData();
      });
    },
    deleteLocationAction() {
      this.$store.dispatch('DELETE_SENDER', this.senderId).then(() => {
        this.$store.dispatch('GET_SENDERS');
        this.deleteLocation = false;
        this.isEdit = false;
        this.togglePopup();
      });
    },
    openNewPopup() {
      this.deleteLocation = false;
      this.isEdit = false;
      this.togglePopup();
    },
    openEditPopup(id) {
      this.deleteLocation = false;
      this.togglePopup();
      this.isEdit = true;
      this.senderId = id;
      const data = this.get_sender_by_id(id);

      this.form = {
        accessible_for_subaccounts: data.accessible_for_subaccounts,
        name: data.name,
        company_name: data.company_name,
        phone: {
          number: data.phone,
          country: '',
        },
        email: data.email,
        vat: data.vat,
        is_default: Number(data.is_default),
        type: data.type,
        contact_type: data.contact_type,
      };
      this.formAddress = {
        address_1: data.address_1,
        address_2: data.address_2,
        post_code: data.post_code,
        city: data.city,
        country_id: data.country_id,
        state: data.state,
      };
    },
  },
  watch: {
    'formAddress.country_id': {
      handler(newVal){
        if(this.form.phone.number.length < 5 && String(newVal) !== ''){
          this.form.phone.country = this.getCountry(newVal, 'alpha2');
        }
      },
      deep: true
    },
  },
};
</script>
